/**
 * AI Configuration Panel Component
 * =================================
 * Settings panel for AI configuration and escalation rules
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Button,
  Slider,
  Typography,
  Divider,
  Grid,
  Alert,
  Chip,
  Stack,
  Tab,
  Tabs,
  CircularProgress
} from '@mui/material';
import {
  Save,
  Psychology,
  Settings,
  Security,
  SmartToy,
  Mic
} from '@mui/icons-material';
import api from '../../services/api';

interface AIConfig {
  llm_provider: 'ollama' | 'openai' | 'groq' | 'anthropic';
  llm_model: string;
  llm_base_url?: string;
  system_prompt?: string;
  temperature: number;
  max_tokens: number;
  top_p: number;
  enable_rag: boolean;
  enable_mcp: boolean;
  enable_streaming: boolean;
  enable_thinking_mode: boolean;
  response_format: 'markdown' | 'plain' | 'html';
  max_response_length: number;
  api_key_configured: boolean;
}

interface EscalationRules {
  // ADDED: 2025-12-04 - Master toggle for auto-escalation
  auto_escalation_enabled: boolean;
  confidence_threshold: number;
  sentiment_threshold: number;
  repetition_threshold: number;
  min_response_chars: number;
  max_ai_interactions: number;
  enable_sentiment_escalation: boolean;
  enable_confidence_escalation: boolean;
  enable_repetition_escalation: boolean;
  enable_timeout_escalation: boolean;
  enable_explicit_request_escalation: boolean;
  default_assignment_type: 'round_robin' | 'manual' | 'load_based';
  notify_agents: boolean;
}

interface STTConfig {
  enabled: boolean;
  provider: 'openai' | 'groq' | 'whisper' | 'ollama' | 'transformers' | 'anthropic';
  openai_api_key?: string;
  groq_api_key?: string;
  anthropic_api_key?: string;
  whisper_model?: string;
  ollama_model?: string;
  transformers_model?: string;
}

export const AIConfigPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiConfig, setAIConfig] = useState<AIConfig | null>(null);
  const [escalationRules, setEscalationRules] = useState<EscalationRules | null>(null);
  const [sttConfig, setSTTConfig] = useState<STTConfig>({
    enabled: false,
    provider: 'openai',
    whisper_model: 'base',
    ollama_model: 'whisper',
    transformers_model: 'openai/whisper-base'
  });
  const [testResult, setTestResult] = useState<any>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);

      // Load AI config and escalation rules
      const response = await api.get('/ai/full-config');
      setAIConfig(response.data.ai_config);
      setEscalationRules(response.data.escalation_rules);

      // Load STT config
      try {
        const sttResponse = await api.get('/speech-to-text/config');
        if (sttResponse.data) {
          setSTTConfig({
            enabled: sttResponse.data.enabled || false,
            provider: sttResponse.data.provider || 'openai',
            openai_api_key: sttResponse.data.openai_api_key,
            groq_api_key: sttResponse.data.groq_api_key,
            anthropic_api_key: sttResponse.data.anthropic_api_key,
            whisper_model: sttResponse.data.whisper_model || 'base',
            ollama_model: sttResponse.data.ollama_model || 'whisper',
            transformers_model: sttResponse.data.transformers_model || 'openai/whisper-base'
          });
          console.log('STT config loaded:', sttResponse.data);
        }
      } catch (sttError) {
        console.error('Failed to load STT config:', sttError);
        // Keep default STT config if loading fails
      }
    } catch (error) {
      console.error('Failed to load AI config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAIConfig = async () => {
    if (!aiConfig) return;

    try {
      setSaving(true);
      await api.put('/ai/config', aiConfig);
      alert('AI configuration saved successfully!');
    } catch (error) {
      console.error('Failed to save AI config:', error);
      alert('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEscalationRules = async () => {
    if (!escalationRules) return;

    try {
      setSaving(true);
      await api.put('/ai/escalation-rules', escalationRules);
      alert('Escalation rules saved successfully!');
    } catch (error) {
      console.error('Failed to save escalation rules:', error);
      alert('Failed to save escalation rules');
    } finally {
      setSaving(false);
    }
  };

  const handleTestProvider = async () => {
    if (!aiConfig) return;

    try {
      setSaving(true);
      setTestResult(null);

      const response = await api.post('/ai/test-provider', {
        llm_provider: aiConfig.llm_provider,
        llm_model: aiConfig.llm_model,
        llm_base_url: aiConfig.llm_base_url
      });

      setTestResult(response.data);
    } catch (error: any) {
      setTestResult({
        success: false,
        error: error.response?.data?.detail || error.message
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        avatar={<SmartToy />}
        title="AI Configuration"
        subheader="Configure AI behavior and escalation rules"
      />
      <CardContent>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="AI Model" icon={<Psychology />} iconPosition="start" />
          <Tab label="Escalation Rules" icon={<Security />} iconPosition="start" />
          <Tab label="Advanced" icon={<Settings />} iconPosition="start" />
          <Tab label="STT Services" icon={<Mic />} iconPosition="start" />
        </Tabs>

        <Divider sx={{ my: 2 }} />

        {/* AI Model Tab */}
        {activeTab === 0 && aiConfig && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              AI Configuration uses the LLM settings from Co-Pilot.
              <Button
                size="small"
                sx={{ ml: 2 }}
                onClick={() => window.location.href = '/settings/copilot'}
              >
                Configure LLM in Co-Pilot Settings
              </Button>
            </Alert>
            <Grid container spacing={3}>
              {/* Current Provider (Read-only) */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Current LLM Provider"
                  value={aiConfig.llm_provider}
                  InputProps={{ readOnly: true }}
                  helperText="Managed in Co-Pilot settings"
                />
              </Grid>

              {/* Current Model (Read-only) */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Current Model"
                  value={aiConfig.llm_model}
                  InputProps={{ readOnly: true }}
                  helperText="Managed in Co-Pilot settings"
                />
              </Grid>

              {/* API Key Status */}
              <Grid item xs={12}>
                <Alert severity={aiConfig.api_key_configured ? 'success' : 'warning'}>
                  API Key: {aiConfig.api_key_configured ? 'Configured ✓' : 'Not configured - configure in Co-Pilot settings'}
                </Alert>
              </Grid>

              {/* System Prompt */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="System Prompt"
                  value={aiConfig.system_prompt || ''}
                  onChange={(e) => setAIConfig({ ...aiConfig, system_prompt: e.target.value })}
                  placeholder="You are a helpful customer support assistant..."
                />
              </Grid>

              {/* Temperature */}
              <Grid item xs={12}>
                <Typography gutterBottom>Temperature: {aiConfig.temperature}</Typography>
                <Slider
                  value={aiConfig.temperature}
                  onChange={(e, v) => setAIConfig({ ...aiConfig, temperature: v as number })}
                  min={0}
                  max={2}
                  step={0.1}
                  marks
                  valueLabelDisplay="auto"
                />
              </Grid>

              {/* Max Tokens */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Tokens"
                  value={aiConfig.max_tokens}
                  onChange={(e) => setAIConfig({ ...aiConfig, max_tokens: parseInt(e.target.value) })}
                  inputProps={{ min: 100, max: 1000000 }}
                  helperText="Maximum tokens in response (up to 1M for supported models)"
                />
              </Grid>

              {/* Top P */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Top P"
                  value={aiConfig.top_p}
                  onChange={(e) => setAIConfig({ ...aiConfig, top_p: parseFloat(e.target.value) })}
                  inputProps={{ step: 0.1, min: 0, max: 1 }}
                />
              </Grid>

              {/* Feature Toggles */}
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={aiConfig.enable_streaming}
                        onChange={(e) => setAIConfig({ ...aiConfig, enable_streaming: e.target.checked })}
                      />
                    }
                    label="Enable Streaming"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={aiConfig.enable_rag}
                        onChange={(e) => setAIConfig({ ...aiConfig, enable_rag: e.target.checked })}
                      />
                    }
                    label="Enable RAG (Knowledge Base)"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={aiConfig.enable_mcp}
                        onChange={(e) => setAIConfig({ ...aiConfig, enable_mcp: e.target.checked })}
                      />
                    }
                    label="Enable MCP Tools"
                  />
                </Stack>
              </Grid>

              {/* Test Connection */}
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  onClick={handleTestProvider}
                  disabled={saving}
                  fullWidth
                >
                  {saving ? 'Testing...' : 'Test Connection'}
                </Button>
                {testResult && (
                  <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
                    {testResult.success ? testResult.message : testResult.error}
                  </Alert>
                )}
              </Grid>

              {/* Save Button */}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveAIConfig}
                  disabled={saving}
                  fullWidth
                >
                  {saving ? 'Saving...' : 'Save AI Configuration'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Escalation Rules Tab */}
        {activeTab === 1 && escalationRules && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Configure when conversations should be escalated from AI to human agents
            </Alert>
            <Grid container spacing={3}>
              {/* Confidence Threshold */}
              <Grid item xs={12}>
                <Typography gutterBottom>
                  Confidence Threshold: {escalationRules.confidence_threshold}
                </Typography>
                <Slider
                  value={escalationRules.confidence_threshold}
                  onChange={(e, v) => setEscalationRules({ ...escalationRules, confidence_threshold: v as number })}
                  min={0}
                  max={1}
                  step={0.05}
                  marks
                  valueLabelDisplay="auto"
                />
                <Typography variant="caption" color="text.secondary">
                  Escalate when AI confidence is below this threshold
                </Typography>
              </Grid>

              {/* Max AI Interactions */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max AI Interactions"
                  value={escalationRules.max_ai_interactions}
                  onChange={(e) => setEscalationRules({ ...escalationRules, max_ai_interactions: parseInt(e.target.value) })}
                  helperText="Maximum number of AI messages before escalation"
                />
              </Grid>

              {/* Min Response Characters */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Min Response Characters"
                  value={escalationRules.min_response_chars}
                  onChange={(e) => setEscalationRules({ ...escalationRules, min_response_chars: parseInt(e.target.value) })}
                  helperText="Minimum characters for valid AI response"
                />
              </Grid>

              {/* Escalation Triggers */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Escalation Triggers
                </Typography>
                <Stack spacing={1}>
                  {/* ADDED: 2025-12-04 - Master toggle for auto-escalation */}
                  <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={escalationRules.auto_escalation_enabled}
                          onChange={(e) => setEscalationRules({ ...escalationRules, auto_escalation_enabled: e.target.checked })}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            Auto-Escalation Enabled
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Master switch to enable/disable all automated escalation triggers. When disabled, only manual escalation will work.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={escalationRules.enable_confidence_escalation}
                        onChange={(e) => setEscalationRules({ ...escalationRules, enable_confidence_escalation: e.target.checked })}
                        disabled={!escalationRules.auto_escalation_enabled}
                      />
                    }
                    label="Low Confidence"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={escalationRules.enable_sentiment_escalation}
                        onChange={(e) => setEscalationRules({ ...escalationRules, enable_sentiment_escalation: e.target.checked })}
                        disabled={!escalationRules.auto_escalation_enabled}
                      />
                    }
                    label="Negative Sentiment"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={escalationRules.enable_repetition_escalation}
                        onChange={(e) => setEscalationRules({ ...escalationRules, enable_repetition_escalation: e.target.checked })}
                        disabled={!escalationRules.auto_escalation_enabled}
                      />
                    }
                    label="Repetitive Responses"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={escalationRules.enable_explicit_request_escalation}
                        onChange={(e) => setEscalationRules({ ...escalationRules, enable_explicit_request_escalation: e.target.checked })}
                        disabled={!escalationRules.auto_escalation_enabled}
                      />
                    }
                    label="Explicit Human Request"
                  />
                </Stack>
              </Grid>

              {/* Assignment Type */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Agent Assignment</InputLabel>
                  <Select
                    value={escalationRules.default_assignment_type}
                    onChange={(e) => setEscalationRules({ ...escalationRules, default_assignment_type: e.target.value as any })}
                  >
                    <MenuItem value="round_robin">Round Robin</MenuItem>
                    <MenuItem value="load_based">Load Based</MenuItem>
                    <MenuItem value="manual">Manual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Notifications */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={escalationRules.notify_agents}
                      onChange={(e) => setEscalationRules({ ...escalationRules, notify_agents: e.target.checked })}
                    />
                  }
                  label="Notify agents on escalation"
                />
              </Grid>

              {/* Save Button */}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveEscalationRules}
                  disabled={saving}
                  fullWidth
                >
                  {saving ? 'Saving...' : 'Save Escalation Rules'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Advanced Tab */}
        {activeTab === 2 && aiConfig && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Advanced AI configuration options
            </Alert>
            <Grid container spacing={3}>
              {/* Response Format */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Response Format</InputLabel>
                  <Select
                    value={aiConfig.response_format}
                    onChange={(e) => setAIConfig({ ...aiConfig, response_format: e.target.value as any })}
                    label="Response Format"
                  >
                    <MenuItem value="markdown">Markdown</MenuItem>
                    <MenuItem value="plain">Plain Text</MenuItem>
                    <MenuItem value="html">HTML</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Max Response Length */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Response Length"
                  value={aiConfig.max_response_length}
                  onChange={(e) => setAIConfig({ ...aiConfig, max_response_length: parseInt(e.target.value) })}
                  helperText="Maximum characters in AI response"
                />
              </Grid>

              {/* Top P */}
              <Grid item xs={12}>
                <Typography gutterBottom>Top P (Nucleus Sampling): {aiConfig.top_p}</Typography>
                <Slider
                  value={aiConfig.top_p}
                  onChange={(e, v) => setAIConfig({ ...aiConfig, top_p: v as number })}
                  min={0}
                  max={1}
                  step={0.05}
                  marks
                  valueLabelDisplay="auto"
                />
                <Typography variant="caption" color="text.secondary">
                  Controls diversity of responses
                </Typography>
              </Grid>

              {/* Enable Thinking Mode */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={aiConfig.enable_thinking_mode}
                      onChange={(e) => setAIConfig({ ...aiConfig, enable_thinking_mode: e.target.checked })}
                    />
                  }
                  label="Enable Thinking Mode (Extended Thinking)"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Allows AI to show its reasoning process before responding
                </Typography>
              </Grid>

              {/* Save Button */}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveAIConfig}
                  disabled={saving}
                  fullWidth
                >
                  {saving ? 'Saving...' : 'Save Advanced Settings'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* STT Services Tab */}
        {activeTab === 3 && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Configure Speech-to-Text service for voice message transcription
            </Alert>
            <Grid container spacing={3}>
              {/* Enable STT */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={sttConfig.enabled}
                      onChange={(e) => setSTTConfig({ ...sttConfig, enabled: e.target.checked })}
                    />
                  }
                  label="Enable Speech-to-Text Service"
                />
              </Grid>

              {/* Provider Selection */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!sttConfig.enabled}>
                  <InputLabel>STT Provider</InputLabel>
                  <Select
                    value={sttConfig.provider}
                    onChange={(e) => setSTTConfig({ ...sttConfig, provider: e.target.value as any })}
                    label="STT Provider"
                  >
                    <MenuItem value="openai">OpenAI Whisper API</MenuItem>
                    <MenuItem value="groq">Groq Whisper</MenuItem>
                    <MenuItem value="whisper">Whisper (Local)</MenuItem>
                    <MenuItem value="ollama">Ollama (Local)</MenuItem>
                    <MenuItem value="transformers">Hugging Face Transformers (Local)</MenuItem>
                    <MenuItem value="anthropic">Anthropic (Coming Soon)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* API Key for OpenAI */}
              {sttConfig.provider === 'openai' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="OpenAI API Key"
                    value={sttConfig.openai_api_key || ''}
                    onChange={(e) => setSTTConfig({ ...sttConfig, openai_api_key: e.target.value })}
                    disabled={!sttConfig.enabled}
                    helperText={
                      sttConfig.openai_api_key?.includes('...') || sttConfig.openai_api_key?.includes('***')
                        ? "API key is configured (masked for security). Enter new key to update."
                        : "Enter your OpenAI API key for Whisper API access"
                    }
                    placeholder={
                      sttConfig.openai_api_key?.includes('...') || sttConfig.openai_api_key?.includes('***')
                        ? "Enter new key to update"
                        : "sk-..."
                    }
                  />
                </Grid>
              )}

              {/* API Key for Groq */}
              {sttConfig.provider === 'groq' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Groq API Key"
                    value={sttConfig.groq_api_key || ''}
                    onChange={(e) => setSTTConfig({ ...sttConfig, groq_api_key: e.target.value })}
                    disabled={!sttConfig.enabled}
                    helperText={
                      sttConfig.groq_api_key?.includes('...') || sttConfig.groq_api_key?.includes('***')
                        ? "API key is configured (masked for security). Enter new key to update."
                        : "Enter your Groq API key for Whisper access"
                    }
                    placeholder={
                      sttConfig.groq_api_key?.includes('...') || sttConfig.groq_api_key?.includes('***')
                        ? "Enter new key to update"
                        : "gsk_..."
                    }
                  />
                </Grid>
              )}

              {/* API Key for Anthropic */}
              {sttConfig.provider === 'anthropic' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Anthropic API Key"
                    value={sttConfig.anthropic_api_key || ''}
                    onChange={(e) => setSTTConfig({ ...sttConfig, anthropic_api_key: e.target.value })}
                    disabled={!sttConfig.enabled}
                    helperText={
                      sttConfig.anthropic_api_key?.includes('...') || sttConfig.anthropic_api_key?.includes('***')
                        ? "API key is configured (masked for security). Anthropic STT coming soon."
                        : "Anthropic STT service coming soon"
                    }
                    placeholder={
                      sttConfig.anthropic_api_key?.includes('...') || sttConfig.anthropic_api_key?.includes('***')
                        ? "Enter new key to update"
                        : "sk-ant-..."
                    }
                  />
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Anthropic STT service is not yet available
                  </Alert>
                </Grid>
              )}

              {/* Local Whisper Model Selection */}
              {sttConfig.provider === 'whisper' && (
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={!sttConfig.enabled}>
                    <InputLabel>Whisper Model</InputLabel>
                    <Select
                      value={sttConfig.whisper_model || 'base'}
                      onChange={(e) => setSTTConfig({ ...sttConfig, whisper_model: e.target.value })}
                      label="Whisper Model"
                    >
                      <MenuItem value="tiny">Tiny (Fastest, Lower Accuracy)</MenuItem>
                      <MenuItem value="base">Base (Balanced)</MenuItem>
                      <MenuItem value="small">Small (Good Quality)</MenuItem>
                      <MenuItem value="medium">Medium (Better Quality)</MenuItem>
                      <MenuItem value="large">Large (Best Quality, Slower)</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Local Whisper will download the model on first use
                  </Typography>
                </Grid>
              )}

              {/* Ollama Model */}
              {sttConfig.provider === 'ollama' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Ollama Model"
                    value={sttConfig.ollama_model || 'whisper'}
                    onChange={(e) => setSTTConfig({ ...sttConfig, ollama_model: e.target.value })}
                    disabled={!sttConfig.enabled}
                    helperText="Make sure Ollama is running locally and the model is pulled"
                  />
                </Grid>
              )}

              {/* Transformers Model */}
              {sttConfig.provider === 'transformers' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Transformers Model"
                    value={sttConfig.transformers_model || 'openai/whisper-base'}
                    onChange={(e) => setSTTConfig({ ...sttConfig, transformers_model: e.target.value })}
                    disabled={!sttConfig.enabled}
                    helperText="Hugging Face model ID (e.g., openai/whisper-base, openai/whisper-large-v3)"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Model will be downloaded from Hugging Face on first use
                  </Typography>
                </Grid>
              )}

              {/* Provider Info */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Provider Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {sttConfig.provider === 'openai' && 'OpenAI Whisper API: Cloud-based, requires API key, fast and accurate'}
                      {sttConfig.provider === 'groq' && 'Groq Whisper: Cloud-based, requires API key, ultra-fast inference'}
                      {sttConfig.provider === 'whisper' && 'Local Whisper: Self-hosted, no API key needed, runs on your server'}
                      {sttConfig.provider === 'ollama' && 'Ollama: Local LLM platform, no API key needed, requires Ollama to be running'}
                      {sttConfig.provider === 'transformers' && 'Hugging Face Transformers: Local ML models, no API key needed, flexible model selection'}
                      {sttConfig.provider === 'anthropic' && 'Anthropic: Coming soon'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Save Button */}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={async () => {
                    try {
                      setSaving(true);

                      // Filter out masked API keys (don't send them to backend)
                      const isMasked = (key: string | undefined) => {
                        return key && (key.includes('...') || key.includes('***'));
                      };

                      const configToSave = {
                        enabled: sttConfig.enabled,
                        provider: sttConfig.provider,
                        // Only include API keys if they're not masked (i.e., user entered new ones)
                        openai_api_key: !isMasked(sttConfig.openai_api_key) ? sttConfig.openai_api_key : undefined,
                        groq_api_key: !isMasked(sttConfig.groq_api_key) ? sttConfig.groq_api_key : undefined,
                        anthropic_api_key: !isMasked(sttConfig.anthropic_api_key) ? sttConfig.anthropic_api_key : undefined,
                        whisper_model: sttConfig.whisper_model,
                        ollama_model: sttConfig.ollama_model,
                        transformers_model: sttConfig.transformers_model
                      };

                      await api.put('/speech-to-text/config', configToSave);
                      alert('STT configuration saved successfully!');

                      // Reload config to get updated values
                      await loadConfig();
                    } catch (error) {
                      console.error('Failed to save STT config:', error);
                      alert('Failed to save STT configuration');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving}
                  fullWidth
                >
                  {saving ? 'Saving...' : 'Save STT Configuration'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AIConfigPanel;
